import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, microsoftLogin } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { msEnabled, getMicrosoftToken, isUserCancel } from '../services/msAuth';

const ROLES = [
  { id: 'admin',      label: 'Admin',      icon: '🛡️' },
  { id: 'consultant', label: 'Consultant', icon: '💼' },
  { id: 'user',       label: 'User',       icon: '👤' },
];

// Local videos served from public/ — no CDN, no CORS, always plays.
const ROLE_VIDEOS = {
  admin:      '/login-admin.mp4?v=5',
  consultant: '/login-consultant.mp4',
  user:       '/login-user.mp4',
};
const DARK_VIDEO = '/login-dark.mp4';

/* ── ITSM logo, recreated as inline SVG (blue ticket + gear/check + headset) ── */
function ItsmIcon({ size = 120 }) {
  const teeth = Array.from({ length: 8 }, (_, i) => i * 45);
  return (
    <svg width={size} height={size * 0.66} viewBox="0 0 160 106" fill="none"
      style={{ filter: 'drop-shadow(0 8px 20px rgba(2,40,90,0.45))' }}>
      <defs>
        <linearGradient id="itsmTicket" x1="40" y1="20" x2="150" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1e3a8a" />
          <stop offset="0.55" stopColor="#1d6fd0" />
          <stop offset="1" stopColor="#38bdf8" />
        </linearGradient>
      </defs>
      <g transform="rotate(-9 90 56)">
        {/* motion dashes */}
        <g fill="url(#itsmTicket)">
          <rect x="2"  y="40" width="36" height="8" rx="4" />
          <rect x="12" y="56" width="44" height="8" rx="4" />
          <rect x="26" y="72" width="30" height="8" rx="4" />
          <circle cx="6" cy="60" r="4" />
          <circle cx="20" cy="44" r="4" />
        </g>
        {/* ticket body */}
        <path d="M52 28 H146 a8 8 0 0 1 8 8 V48 a8 8 0 0 0 0 16 V76 a8 8 0 0 1 -8 8 H52 a8 8 0 0 1 -8 -8 V36 a8 8 0 0 1 8 -8 Z"
          fill="url(#itsmTicket)" />
        {/* perforation */}
        <line x1="118" y1="30" x2="118" y2="82" stroke="#fff" strokeWidth="2.4"
          strokeDasharray="3 5" strokeLinecap="round" opacity="0.92" />
        {/* star + lines on stub */}
        <path d="M134 40 l2.2 4.6 5 0.5 -3.7 3.4 1 5 -4.5 -2.5 -4.5 2.5 1 -5 -3.7 -3.4 5 -0.5 Z" fill="#fff" />
        <g stroke="#fff" strokeWidth="2.4" strokeLinecap="round" opacity="0.92">
          <line x1="128" y1="60" x2="146" y2="60" />
          <line x1="128" y1="67" x2="142" y2="67" />
        </g>
        {/* headset arc + ear pads + mic */}
        <g stroke="#fff" strokeWidth="4.4" fill="#fff" strokeLinecap="round">
          <path d="M68 56 a18 18 0 0 1 36 0" fill="none" />
          <rect x="64" y="54" width="7" height="15" rx="3.5" stroke="none" />
          <rect x="101" y="54" width="7" height="15" rx="3.5" stroke="none" />
          <path d="M104 67 v4 a6 6 0 0 1 -6 6 H88" fill="none" strokeWidth="3.4" />
          <circle cx="86" cy="77" r="0.5" />
        </g>
        {/* gear */}
        <g fill="#fff">
          {teeth.map(a => (
            <rect key={a} x="84.5" y="38" width="3" height="7" rx="1.4"
              transform={`rotate(${a} 86 56)`} />
          ))}
          <circle cx="86" cy="56" r="13" fill="#fff" />
          <circle cx="86" cy="56" r="8.5" fill="url(#itsmTicket)" />
          {/* check inside gear */}
          <path d="M82 56 l3 3 5.5 -6" stroke="#fff" strokeWidth="2.6" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [role,     setRole]     = useState('admin');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,    setLoading]    = useState(false);
  const [msLoading,  setMsLoading]  = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [collapsed,  setCollapsed]  = useState(false);
  const videoRef = useRef(null);

  const isDark = theme === 'dark';
  const activeVideo = isDark ? DARK_VIDEO : ROLE_VIDEOS[role];

  useEffect(() => { setVideoReady(false); }, [activeVideo]);

  const pickRole = (r) => { setRole(r); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await login({ email, password });
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleMicrosoft = async () => {
    setError('');
    if (!msEnabled) {
      setError('Microsoft sign-in is not configured yet. Please add the Azure credentials to enable it.');
      return;
    }
    setMsLoading(true);
    try {
      const msToken = await getMicrosoftToken();
      const res = await microsoftLogin(msToken);
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      if (!isUserCancel(err)) {
        setError(err.response?.data?.error || 'Microsoft sign-in failed. Please try again.');
      }
    } finally { setMsLoading(false); }
  };

  // Form panel (left) — adapts to theme since it sits on a solid surface.
  const c = isDark
    ? { panel: '#0b1120', text: '#f8fafc', sub: '#94a3b8', inputBg: '#121a2b',
        inputBdr: '#243047', inputText: '#f8fafc', tabBar: '#0f1729', tabBdr: '#1e293b' }
    : { panel: '#ffffff', text: '#0f172a', sub: '#64748b', inputBg: '#f1f5f9',
        inputBdr: '#e2e8f0', inputText: '#0f172a', tabBar: '#f1f5f9', tabBdr: '#e2e8f0' };

  return (
    <div className={`login-root${collapsed ? ' collapsed' : ''}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Theme toggle */}
      <button onClick={toggleTheme} title={isDark ? 'Light mode' : 'Dark mode'}
        style={{
          position: 'fixed', top: 16, right: 18, zIndex: 300,
          width: 38, height: 38, borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)',
          backdropFilter: 'blur(8px)', fontSize: 16, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        }}>{isDark ? '☀️' : '🌙'}</button>

      {/* ════════ LEFT: login form ════════ */}
      <div className="login-form-panel" style={{ background: c.panel }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          {/* compact brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 26 }}>
            <ItsmIcon size={54} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, letterSpacing: '-0.3px', lineHeight: 1 }}>ITSM Tool</div>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: c.sub, letterSpacing: '1.5px', marginTop: 3 }}>IT SERVICE MANAGEMENT</div>
            </div>
          </div>

          <h2 style={{ fontSize: 26, fontWeight: 800, color: c.text, marginBottom: 5, letterSpacing: '-0.5px' }}>Welcome back</h2>
          <p style={{ fontSize: 13.5, fontWeight: 500, color: c.sub, marginBottom: 24 }}>Sign in to continue to your portal</p>

          {/* Role tabs */}
          <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, letterSpacing: '1.2px', marginBottom: 8 }}>LOGIN AS</div>
          <div style={{ display: 'flex', gap: 5, marginBottom: 20, background: c.tabBar, borderRadius: 11, padding: 4, border: `1px solid ${c.tabBdr}` }}>
            {ROLES.map(r => (
              <button key={r.id} type="button" onClick={() => pickRole(r.id)} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                padding: '9px 4px', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s',
                background: role === r.id ? 'linear-gradient(135deg,var(--primary),var(--primary-dark))' : 'transparent',
                color: role === r.id ? '#fff' : c.sub,
                boxShadow: role === r.id ? '0 4px 14px var(--primary-light)' : 'none',
              }}>
                <span style={{ fontSize: 14 }}>{r.icon}</span>{r.label}
              </button>
            ))}
          </div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
              color: '#dc2626', borderRadius: 9, padding: '9px 12px', fontSize: 12.5, marginBottom: 14, fontWeight: 600 }}>⚠ {error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Email */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, letterSpacing: '1.2px', marginBottom: 7 }}>USERNAME OR EMAIL</div>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c.sub} strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email" required
                  style={{ width: '100%', padding: '12px 14px 12px 40px', background: c.inputBg,
                    border: `1.5px solid ${c.inputBdr}`, borderRadius: 10, color: c.inputText,
                    fontSize: 14, fontWeight: 500, outline: 'none', fontFamily: "'DM Sans', sans-serif",
                    transition: 'border-color 0.2s' }}
                  onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={e => e.target.style.borderColor = c.inputBdr} />
              </div>
            </div>
            {/* Password */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, letterSpacing: '1.2px', marginBottom: 7 }}>PASSWORD</div>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c.sub} strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input type={showPwd ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required
                  style={{ width: '100%', padding: '12px 42px 12px 40px', background: c.inputBg,
                    border: `1.5px solid ${c.inputBdr}`, borderRadius: 10, color: c.inputText,
                    fontSize: 14, fontWeight: 500, outline: 'none', fontFamily: "'DM Sans', sans-serif",
                    transition: 'border-color 0.2s' }}
                  onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={e => e.target.style.borderColor = c.inputBdr} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                  position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  display: 'flex', alignItems: 'center', color: c.sub }}>
                  {showPwd
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18 18 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18 18 0 0 1-2.16 3.19"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                </button>
              </div>
            </div>
            {/* Submit */}
            <button type="submit" disabled={loading} style={{
              marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px', background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))',
              border: 'none', borderRadius: 11, color: '#fff', fontSize: 14.5, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.75 : 1,
              boxShadow: '0 6px 20px rgba(2,132,199,0.4)', transition: 'opacity 0.2s, transform 0.1s',
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.2px' }}>
              {loading ? <><span style={spinnerSt} /> Signing in…</> : `Sign In as ${ROLES.find(r => r.id === role)?.label}`}
            </button>
          </form>

          {(
            <>
              {/* divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 16px' }}>
                <span style={{ flex: 1, height: 1, background: c.inputBdr }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: c.sub, letterSpacing: '0.5px' }}>OR</span>
                <span style={{ flex: 1, height: 1, background: c.inputBdr }} />
              </div>

              {/* Microsoft sign-in */}
              <button type="button" onClick={handleMicrosoft} disabled={msLoading}
                className="ms-signin-btn"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '12px', borderRadius: 11, cursor: msLoading ? 'not-allowed' : 'pointer',
                  background: isDark ? '#1b2436' : '#ffffff', border: `1.5px solid ${c.inputBdr}`,
                  color: c.text, fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                  opacity: msLoading ? 0.7 : 1, transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                }}>
                {msLoading ? <span style={{ ...spinnerSt, borderColor: 'rgba(0,0,0,0.2)', borderTopColor: c.text }} />
                  : (
                    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                  )}
                {msLoading ? 'Signing in…' : 'Sign in with Microsoft'}
              </button>
            </>
          )}

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12, fontWeight: 500, color: c.sub }}>
            Contact your administrator for credentials
          </p>
        </div>

        {/* Collapse toggle (←) — hides the login panel */}
        <button type="button" className="collapse-toggle" onClick={() => setCollapsed(true)}
          title="Hide login panel" aria-label="Hide login panel">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* ════════ RIGHT: branding / details ════════ */}
      <div className="login-brand-panel">
        {/* background gradient */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0,
          background: isDark
            ? 'linear-gradient(160deg,#05060f 0%,#0a1230 45%,#13204d 75%,#243a6b 100%)'
            : 'linear-gradient(150deg,#0f2942 0%,#0a4a7c 35%,#0369a1 65%,#0284c7 85%,#0ea5e9 100%)',
          animation: isDark ? 'none' : 'bgShift 14s ease-in-out infinite alternate' }}>
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.12 }}>
            <defs><pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#38bdf8" strokeWidth="0.7"/></pattern></defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
          <div style={{ position:'absolute', top:'12%', right:'12%', width:340, height:340, borderRadius:'50%',
            background:'radial-gradient(circle,rgba(56,189,248,0.22) 0%,transparent 70%)', animation:'pulse1 7s ease-in-out infinite' }} />
          <div style={{ position:'absolute', bottom:'14%', left:'8%', width:300, height:300, borderRadius:'50%',
            background:'radial-gradient(circle,rgba(14,165,233,0.18) 0%,transparent 70%)', animation:'pulse2 9s ease-in-out infinite' }} />
        </div>

        {/* role video */}
        <video key={activeVideo} ref={videoRef} src={activeVideo} autoPlay muted loop playsInline
          onCanPlay={() => setVideoReady(true)} onError={() => setVideoReady(false)}
          style={{ position: 'absolute', inset: 0, zIndex: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: videoReady ? 1 : 0, transition: 'opacity 1.2s ease' }} />

        {/* content */}
        <div className="brand-content" style={{ position: 'relative', zIndex: 2, maxWidth: 440, padding: '0 8px', textAlign: 'center' }}>
          <div className="brand-logo-wrap" style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <div
              className={`brand-logo${collapsed ? ' clickable' : ''}`}
              onClick={() => collapsed && setCollapsed(false)}
              role={collapsed ? 'button' : undefined}
              title={collapsed ? 'Click to sign in' : undefined}
            >
              <ItsmIcon size={150} />
            </div>
          </div>
          <div style={{ fontSize: 52, fontWeight: 800, color: '#fff', letterSpacing: '2px', lineHeight: 1, textShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>ITSM</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.82)', letterSpacing: '4px', marginTop: 8, marginBottom: 22 }}>IT SERVICE MANAGEMENT</div>
          <p style={{ fontSize: 15.5, fontWeight: 500, color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, marginBottom: 4 }}>
            Raise, track and resolve IT support tickets — all in one simple place.
          </p>

          {/* Expand hint — only when collapsed */}
          <button type="button" className="expand-hint" onClick={() => setCollapsed(false)} aria-label="Show login panel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            Click to sign in
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes bgShift {
          0%   { background-position: 0% 50%; filter: hue-rotate(0deg); }
          50%  { background-position: 100% 50%; filter: hue-rotate(12deg); }
          100% { background-position: 0% 50%; filter: hue-rotate(0deg); }
        }
        @keyframes pulse1 { 0%,100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.25); opacity: 1; } }
        @keyframes pulse2 { 0%,100% { transform: scale(1.1); opacity: 0.7; } 50% { transform: scale(0.9); opacity: 1; } }
        @keyframes twinkle { 0%,100% { opacity: 0.15; } 50% { opacity: 0.95; } }
        @keyframes floatY { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes hintPulse { 0%,100% { transform: translateY(0); box-shadow: 0 6px 18px rgba(2,132,199,0.35); } 50% { transform: translateY(-4px); box-shadow: 0 12px 26px rgba(56,189,248,0.55); } }
        @keyframes sheen { 0% { left: -60%; } 60%,100% { left: 130%; } }
        * { box-sizing: border-box; }

        html, body, #root { height: 100%; margin: 0; overflow: hidden; }
        .login-root { position: fixed; inset: 0; display: flex; height: 100vh; width: 100vw; overflow: hidden; }

        /* ── Left: login form (collapsible) ── */
        .login-form-panel {
          flex: 0 0 46%; max-width: 560px;
          display: flex; align-items: center; justify-content: center;
          padding: 40px 56px; position: relative;
          transition: flex-basis 0.6s cubic-bezier(0.76,0,0.24,1),
                      max-width 0.6s cubic-bezier(0.76,0,0.24,1),
                      padding 0.6s cubic-bezier(0.76,0,0.24,1),
                      opacity 0.4s ease, transform 0.6s cubic-bezier(0.76,0,0.24,1);
        }
        .login-form-panel > div:first-child { animation: fadeUp 0.6s ease both; }
        .login-root.collapsed .login-form-panel {
          flex-basis: 0; max-width: 0; padding-left: 0; padding-right: 0;
          opacity: 0; transform: translateX(-70px); overflow: hidden; pointer-events: none;
        }

        /* ── Right: brand panel ── */
        .login-brand-panel {
          flex: 1; position: relative; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
          padding: 48px;
        }
        .brand-content { transition: transform 0.6s cubic-bezier(0.34,1.56,0.64,1); animation: fadeUp 0.7s ease both; }
        .login-root.collapsed .brand-content { transform: scale(1.12); }

        /* clickable logo (no continuous motion) */
        .brand-logo-wrap { }
        .brand-logo { display: inline-flex; transition: transform 0.45s cubic-bezier(0.34,1.56,0.64,1), filter 0.45s ease; }
        .brand-logo svg { filter: drop-shadow(0 12px 30px rgba(2,40,90,0.5)); }
        .brand-logo.clickable { cursor: pointer; }
        .brand-logo.clickable:hover { transform: scale(1.09) rotate(-2deg); filter: drop-shadow(0 0 34px rgba(56,189,248,0.85)); }
        .brand-logo.clickable:active { transform: scale(1.02); }

        /* collapse toggle (←) */
        .collapse-toggle {
          position: absolute; right: -21px; top: 50%; transform: translateY(-50%);
          width: 42px; height: 42px; border-radius: 50%;
          background: linear-gradient(135deg,var(--primary),var(--primary-dark));
          color: #fff; border: 3px solid ${c.panel}; cursor: pointer; z-index: 5;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 18px rgba(2,132,199,0.45);
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
        }
        .collapse-toggle:hover { transform: translateY(-50%) scale(1.15) rotate(-8deg); box-shadow: 0 10px 26px rgba(56,189,248,0.7); }
        .collapse-toggle:active { transform: translateY(-50%) scale(0.95); }
        .login-root.collapsed .collapse-toggle { opacity: 0; pointer-events: none; }

        /* expand hint pill */
        .expand-hint {
          margin-top: 22px; display: inline-flex; align-items: center; gap: 7px;
          padding: 11px 22px; border: none; border-radius: 40px;
          background: linear-gradient(135deg,var(--primary),var(--primary-dark));
          color: #fff; font-size: 13.5px; font-weight: 700; cursor: pointer;
          font-family: 'DM Sans', sans-serif; letter-spacing: 0.3px;
          box-shadow: 0 6px 18px rgba(2,132,199,0.4);
          opacity: 0; transform: translateY(10px) scale(0.9); pointer-events: none;
          transition: opacity 0.4s ease 0.25s, transform 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.25s;
        }
        .expand-hint svg { margin-right: 5px; }
        .expand-hint:hover { filter: brightness(1.08); }
        .login-root.collapsed .expand-hint { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }

        /* form micro-interactions */
        .login-form-panel form button[type=submit] { position: relative; overflow: hidden; }
        .login-form-panel form button[type=submit]:not(:disabled):hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(2,132,199,0.55); }
        .login-form-panel form button[type=submit]:not(:disabled):active { transform: translateY(0); }
        .login-form-panel form button[type=submit]::after {
          content: ''; position: absolute; top: 0; left: -60%; width: 45%; height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,0.35), transparent);
          transform: skewX(-20deg);
        }
        .login-form-panel form button[type=submit]:not(:disabled):hover::after { animation: sheen 0.9s ease; }
        .login-form-panel input { transition: border-color 0.2s, box-shadow 0.2s, background 0.2s; }
        .login-form-panel input:focus { box-shadow: 0 0 0 4px var(--primary-light); }
        .login-form-panel input::placeholder { color: ${isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8'} !important; }
        .ms-signin-btn:not(:disabled):hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.14); border-color: var(--primary) !important; }
        .ms-signin-btn:not(:disabled):active { transform: translateY(0); }

        @media (max-width: 900px) {
          .login-root { flex-direction: column; }
          .login-brand-panel { order: -1; flex: none; min-height: 240px; padding: 36px 24px; }
          .login-form-panel { flex: none; max-width: none; width: 100%; padding: 36px 24px 48px; }
          .collapse-toggle { right: 50%; top: auto; bottom: -21px; transform: translateX(50%); }
          .collapse-toggle:hover { transform: translateX(50%) scale(1.12); }
          .login-root.collapsed .login-form-panel { flex-basis: 0; min-height: 0; max-width: none; padding: 0; height: 0; transform: translateY(-40px); }
        }
      `}</style>
    </div>
  );
}

const spinnerSt = {
  display: 'inline-block', width: 14, height: 14,
  border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
  borderRadius: '50%', animation: 'spin 0.7s linear infinite',
};
